import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, ShoppingCart, ArrowRight } from "lucide-react";
import { createBuyerInterest } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1600&q=80";

export default function BuyerInterest() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    company_name: "", contact_person: "", phone: "", email: "",
    buyer_type: "wholesale", city: "", country: "India",
    commodities_of_interest: "", monthly_volume: "", notes: "", consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const cleanPhone = form.phone.replace(/\D/g, "").slice(-10);
    if (cleanPhone.length !== 10) { toast.error(t("farmer_interest.invalid_phone")); return; }
    if (!form.consent) { toast.error(t("farmer_interest.consent_required")); return; }
    if (!form.company_name || !form.contact_person || !form.email || !form.city) { toast.error(t("toast.fill_required")); return; }
    setBusy(true);
    try {
      await createBuyerInterest({
        company_name: form.company_name,
        contact_person: form.contact_person,
        phone: cleanPhone,
        email: form.email,
        buyer_type: form.buyer_type,
        city: form.city,
        country: form.country,
        commodities_of_interest: form.commodities_of_interest ? form.commodities_of_interest.split(",").map((s) => s.trim()).filter(Boolean) : [],
        monthly_volume: form.monthly_volume || null,
        notes: form.notes || null,
        consent: true,
        preferred_language: i18n.language || "en",
      });
      setDone(true);
      toast.success(t("buyer_interest.success_title"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("toast.generic_error"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-buyer-interest">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={HERO} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 tr-hero-overlay" /></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-white">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("buyer_interest.eyebrow")}</div>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">
            {t("buyer_interest.title_a")} <span className="text-[var(--tr-citrus-yellow)]">{t("buyer_interest.title_b")}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85">{t("buyer_interest.body")}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        {done ? (
          <div data-testid="buyer-interest-success" className="tr-card p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-[var(--tr-lime)] mx-auto" />
            <h3 className="font-display font-extrabold text-3xl mt-4 text-[var(--tr-deep-green)]">{t("buyer_interest.success_title")}</h3>
            <p className="mt-4 text-[var(--tr-muted)] max-w-xl mx-auto">{t("buyer_interest.success_body")}</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="buyer-interest-form" className="tr-card p-8 lg:p-10 space-y-6">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-6 h-6 text-[var(--tr-deep-green)]" />
              <h2 className="font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t("buyer_interest.eyebrow")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Fld label={`${t("buyer_interest.f_company")}*`}><input required data-testid="buyer-company" className="tr-input" value={form.company_name} onChange={set("company_name")} /></Fld>
              <Fld label={`${t("buyer_interest.f_contact_person")}*`}><input required data-testid="buyer-contact" className="tr-input" value={form.contact_person} onChange={set("contact_person")} /></Fld>
              <Fld label={`${t("buyer_interest.f_phone")}*`}><input required inputMode="tel" data-testid="buyer-phone" className="tr-input" value={form.phone} onChange={set("phone")} /></Fld>
              <Fld label={`${t("buyer_interest.f_email")}*`}><input required type="email" data-testid="buyer-email" className="tr-input" value={form.email} onChange={set("email")} /></Fld>
              <Fld label={t("buyer_interest.f_type")}>
                <select data-testid="buyer-type" className="tr-input" value={form.buyer_type} onChange={set("buyer_type")}>
                  <option value="retail">{t("buyer_interest.type_retail")}</option>
                  <option value="wholesale">{t("buyer_interest.type_wholesale")}</option>
                  <option value="export">{t("buyer_interest.type_export")}</option>
                  <option value="processor">{t("buyer_interest.type_processor")}</option>
                  <option value="horeca">HoReCa (Hotels, Restaurants & Catering)</option>
                  <option value="other">{t("buyer_interest.type_other")}</option>
                </select>
              </Fld>
              <Fld label={t("buyer_interest.f_volume")}><input data-testid="buyer-volume" className="tr-input" placeholder="e.g. 20 MT / month" value={form.monthly_volume} onChange={set("monthly_volume")} /></Fld>
              <Fld label={`${t("buyer_interest.f_city")}*`}><input required data-testid="buyer-city" className="tr-input" value={form.city} onChange={set("city")} /></Fld>
              <Fld label={t("buyer_interest.f_country")}><input data-testid="buyer-country" className="tr-input" value={form.country} onChange={set("country")} /></Fld>
              <Fld label={t("buyer_interest.f_commodities")} className="md:col-span-2">
                <input data-testid="buyer-commodities" className="tr-input" placeholder={t("buyer_interest.f_commodities_ph")} value={form.commodities_of_interest} onChange={set("commodities_of_interest")} />
              </Fld>
              <Fld label={t("buyer_interest.f_notes")} className="md:col-span-2"><textarea data-testid="buyer-notes" className="tr-input tr-textarea" value={form.notes} onChange={set("notes")} /></Fld>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="buyer-consent" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)]" />
              <span className="text-sm text-[var(--tr-deep-green)] leading-relaxed">{t("buyer_interest.f_consent")}</span>
            </label>
            <button type="submit" disabled={busy} data-testid="buyer-submit" className="tr-pill-btn tr-btn-primary disabled:opacity-60">
              {busy ? t("common.submitting") : t("buyer_interest.f_submit")} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

function Fld({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="tr-label">{label}</span>
      {children}
    </label>
  );
}
